// src/components/Performance123View.jsx

import React, { useState, useRef, useEffect } from 'react';
import { RefreshCw, CheckCircle2, Mic, Video, X } from 'lucide-react';
import { db } from '../config/firebase-config';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { useOrganization } from '../context/OrganizationContext';
import { sortEpisodes } from '../utils/podcastOrder';

// --- 1. REUSABLE YOUTUBE PLAYER (Standardized) ---
const CustomYouTubePlayer = ({ videoId }) => {
    const containerRef = useRef(null);
    const playerRef = useRef(null);
    const [isEnded, setIsEnded] = useState(false);

    useEffect(() => {
        setIsEnded(false); 
        if (!videoId) return;

        const loadAPI = () => {
            return new Promise((resolve) => {
                if (window.YT && window.YT.Player) {
                    resolve(window.YT);
                    return;
                }
                if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
                    const tag = document.createElement('script');
                    tag.src = 'https://www.youtube.com/iframe_api';
                    const firstScriptTag = document.getElementsByTagName('script')[0];
                    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
                }
                window.onYouTubeIframeAPIReady = () => resolve(window.YT);
            });
        };

        let player;
        loadAPI().then((YT) => {
            if (!containerRef.current) return;
            const el = document.createElement('div');
            el.className = "w-full h-full absolute inset-0";
            containerRef.current.innerHTML = '';
            containerRef.current.appendChild(el);
            
            player = new YT.Player(el, {
                videoId: videoId,
                playerVars: { 
                    rel: 0, 
                    modestbranding: 1, 
                    playsinline: 1, 
                    showinfo: 0,
                    autoplay: 1 // Auto-play when modal opens
                },
                events: {
                    onStateChange: (event) => {
                        if (event.data === YT.PlayerState.ENDED) {
                            // FORCE EXIT FULLSCREEN ON END
                            if (document.fullscreenElement) {
                                document.exitFullscreen().catch(() => {});
                            } else if (document.webkitFullscreenElement) { /* Safari */
                                document.webkitExitFullscreen().catch(() => {});
                            }
                            setIsEnded(true);
                        } 
                        else if (event.data === YT.PlayerState.PLAYING) {
                            setIsEnded(false);
                        }
                    }
                }
            });
            playerRef.current = player;
        });

        return () => {
            if (playerRef.current && playerRef.current.destroy) playerRef.current.destroy();
        };
    }, [videoId]);

    return (
        <div className="relative w-full h-full bg-black overflow-hidden shadow-2xl rounded-xl ring-1 ring-white/10">
            <div ref={containerRef} className="absolute inset-0 w-full h-full"></div>
            
            {/* BRANDED END SCREEN OVERLAY */}
            {isEnded && (
                <div className="absolute inset-0 z-50 bg-slate-900 flex flex-col items-center justify-center animate-in fade-in duration-300">
                    <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mb-4 border border-blue-500/20 shadow-[0_0_30px_rgba(59,130,246,0.2)]">
                        <CheckCircle2 size={40} className="text-blue-500" />
                    </div>
                    <h3 className="text-white text-xl font-black mb-6 tracking-tight">Session Complete</h3>
                    <button 
                        onClick={() => { 
                            setIsEnded(false); 
                            if (playerRef.current) playerRef.current.playVideo(); 
                        }} 
                        className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold flex items-center gap-2 transition-colors border border-white/10"
                    >
                        <RefreshCw size={18} /> Replay Video
                    </button>
                </div>
            )}
        </div>
    );
};

// --- 2. AUDIO PLAYER COMPONENT ---
const CustomAudioPlayer = ({ audioUrl, title }) => (
    <div className="w-full bg-slate-50 p-6 rounded-2xl border border-slate-200 flex flex-col gap-4">
        <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-100 text-indigo-600 rounded-full">
                <Mic size={24} />
            </div>
            <div>
                <h4 className="font-bold text-slate-800 text-sm">{title}</h4>
                <p className="text-xs text-slate-500">Audio Playback</p>
            </div>
        </div>
        <audio controls className="w-full h-10 outline-none" src={audioUrl}>
            Your browser does not support the audio element.
        </audio>
    </div>
);

// --- HELPER TO EXTRACT VIDEO ID ---
const getYouTubeVideoId = (url) => {
    if (!url) return null;
    // Handles standard, short, and embed URLs
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
};

const Performance123View = () => {
    const [activeEpisode, setActiveEpisode] = useState(null);
    const [episodes, setEpisodes] = useState([]);
    const [loading, setLoading] = useState(true);

    // VERTICAL FILTER: episodes are a global library. An episode tagged
    // `verticals: ['sport'|'corporate']` only shows to orgs of that industry;
    // untagged ('both') episodes show everywhere. Mirrors the topic packs.
    const { orgData } = useOrganization();
    const industry = orgData ? (orgData.industry === 'corporate' ? 'corporate' : 'sport') : null;
    const visibleEpisodes = episodes.filter(ep =>
        !industry || !Array.isArray(ep.verticals) || ep.verticals.includes(industry)
    );

    // --- 3. FETCH LIVE EPISODES FROM FIRESTORE ---
    useEffect(() => {
        const q = query(collection(db, "performance_123_episodes"), orderBy("createdAt", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(doc => ({ 
                id: doc.id, 
                ...doc.data(),
                color: doc.data().color || "bg-indigo-500",
                icon: "🎙️" 
            }));
            setEpisodes(sortEpisodes(items)); // manual order first, then newest
            setLoading(false);
        }, (error) => {
            console.warn("Performance 123 Sync Error:", error);
            setEpisodes([]);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    return (
        <div className="space-y-8 animate-in fade-in max-w-5xl mx-auto relative pb-20">
            
            {/* MODAL PLAYER OVERLAY */}
            {activeEpisode && (
                <div className="fixed inset-0 z-[200] bg-slate-900/90 backdrop-blur-sm flex items-center justify-center p-2 md:p-4 animate-in fade-in duration-200" onClick={() => setActiveEpisode(null)}>
                    <div 
                        className="bg-white w-full max-w-5xl rounded-3xl overflow-hidden shadow-2xl flex flex-col h-auto max-h-[90vh]"
                        onClick={(e) => e.stopPropagation()} 
                    >
                        {/* Header */}
                        <div className="p-4 md:p-6 border-b border-slate-100 flex justify-between items-start bg-slate-50">
                            <div>
                                <h3 className="text-lg md:text-xl font-black text-slate-900 mb-1">{activeEpisode.title}</h3>
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="font-bold text-brand-primary">{activeEpisode.guest}</span>
                                    <span className="text-slate-400">•</span>
                                    <span className="text-slate-500">{activeEpisode.role}</span>
                                </div>
                            </div>
                            <button onClick={() => setActiveEpisode(null)} className="p-2 hover:bg-slate-200 rounded-full transition-colors">
                                <X size={24} className="text-slate-400" />
                            </button>
                        </div>

                        {/* Player Content Area */}
                        <div className="bg-black flex-1 flex items-center justify-center relative overflow-hidden w-full min-h-[300px]">
                            {activeEpisode.type === 'Video' ? (
                                /* ENFORCED 16:9 ASPECT RATIO WRAPPER */
                                <div 
                                    className="w-full relative" 
                                    style={{ aspectRatio: '16/9', maxHeight: '70vh', maxWidth: '100%' }}
                                >
                                    <CustomYouTubePlayer videoId={getYouTubeVideoId(activeEpisode.url)} />
                                </div>
                            ) : (
                                <div className="w-full h-full bg-slate-50 flex items-center justify-center p-8 min-h-[300px]">
                                    <CustomAudioPlayer audioUrl={activeEpisode.url} title={activeEpisode.title} />
                                </div>
                            )}
                        </div>

                        {/* Description Footer */}
                        <div className="p-6 bg-white border-t border-slate-100">
                            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Episode Synopsis</h4>
                            <p className="text-slate-600 text-sm leading-relaxed">{activeEpisode.content || "No description available."}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* HERO SECTION */}
            <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-3xl p-8 text-white relative overflow-hidden shadow-2xl">
                <div className="relative z-10">
                    <h1 className="text-3xl font-black mb-2">Performance 1-2-3</h1>
                    <p className="text-violet-100 max-w-2xl text-lg">Short, high-impact conversations with world-class performers.</p>
                </div>
            </div>

            {/* EPISODE LIST */}
            {loading ? (
                <div className="p-12 text-center text-slate-400">Loading Episodes...</div>
            ) : visibleEpisodes.length === 0 ? (
                <div className="p-12 text-center border-2 border-dashed border-slate-200 rounded-3xl">
                    <p className="text-slate-400 font-medium">No episodes available yet.</p>
                    <p className="text-xs text-slate-300 mt-2">Check the Content Manager to add content.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4">
                    {visibleEpisodes.map((ep) => (
                        <div 
                            key={ep.id} 
                            onClick={() => setActiveEpisode(ep)} 
                            className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-brand-primary hover:shadow-lg transition-all cursor-pointer group flex items-start gap-6"
                        >
                            {(() => {
                                // YouTube-hosted episodes get the real thumbnail; audio-only keeps the mic tile.
                                const ytId = getYouTubeVideoId(ep.url);
                                return (
                                    <div className={`w-32 h-24 rounded-xl overflow-hidden ${ytId ? 'bg-slate-900' : (ep.type === 'Video' ? 'bg-orange-500' : 'bg-blue-500')} flex items-center justify-center text-4xl text-white shadow-md shrink-0 group-hover:scale-105 transition-transform relative`}>
                                        {ytId ? (
                                            <img
                                                src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`}
                                                alt={ep.title}
                                                loading="lazy"
                                                className="w-full h-full object-cover"
                                            />
                                        ) : ep.icon}
                                        <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 bg-white/95 text-slate-900 text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm border border-slate-100 flex items-center gap-1">
                                            {ep.type === 'Video' ? <Video size={10} /> : <Mic size={10} />} {ep.type}
                                        </div>
                                    </div>
                                );
                            })()}
                            <div className="flex-1 min-w-0 py-1">
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <h3 className="font-bold text-lg text-slate-800 group-hover:text-brand-primary transition-colors line-clamp-1">{ep.title}</h3>
                                        <p className="text-sm font-medium text-slate-500">with {ep.guest} <span className="opacity-50">| {ep.role}</span></p>
                                    </div>
                                    {ep.duration && <span className="px-3 py-1 bg-slate-100 rounded-full text-xs font-bold text-slate-500">{ep.duration}</span>}
                                </div>
                                <p className="text-slate-600 text-sm leading-relaxed line-clamp-2">{ep.content}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Performance123View;